import { useState, useRef, useEffect, useMemo } from "react";

export interface GroupedOption {
  label: string;
  options: string[];
}

interface CreatableMultiSelectProps {
  label?: string;
  values: string[];
  options: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  required?: boolean;
  disabled?: boolean;
  isZh?: boolean;
  hideLabel?: boolean;
  groups?: GroupedOption[];
  onOptionDelete?: (option: string) => void;
  hideClearInDropdown?: boolean;
}

export default function CreatableMultiSelect({
  label,
  values,
  options,
  onChange,
  placeholder,
  required = false,
  disabled = false,
  isZh = false,
  hideLabel = false,
  groups,
  onOptionDelete,
  hideClearInDropdown = false,
}: CreatableMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filtered = useMemo(() => {
    const allOpts = [...new Set([...options, ...values])];
    if (!search.trim()) return allOpts;
    const q = search.toLowerCase();
    return allOpts.filter((o) => o.toLowerCase().includes(q));
  }, [options, values, search]);

  const exactMatch =
    search.trim() && options.some((o) => o.toLowerCase() === search.trim().toLowerCase());
  const canCreate = search.trim() && !exactMatch && !values.includes(search.trim());

  const toggleValue = (val: string) => {
    if (values.includes(val)) {
      onChange(values.filter((v) => v !== val));
    } else {
      onChange([...values, val]);
    }
  };

  const removeValue = (val: string) => {
    onChange(values.filter((v) => v !== val));
  };

  const handleConfirmDelete = () => {
    if (deleteConfirm && onOptionDelete) {
      onOptionDelete(deleteConfirm);
      // Also remove from selected values if it was selected
      if (values.includes(deleteConfirm)) {
        onChange(values.filter((v) => v !== deleteConfirm));
      }
    }
    setDeleteConfirm(null);
  };

  const renderOptionRow = (opt: string) => {
    const selected = values.includes(opt);
    return (
      <button
        key={opt}
        type="button"
        onClick={() => toggleValue(opt)}
        className={`w-full px-3 py-2 text-sm text-left cursor-pointer hover:bg-pink-50 transition-colors flex items-center gap-2 ${
          selected ? "text-[#830051] font-medium" : "text-gray-700"
        }`}
      >
        <span
          className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
            selected ? "border-[#830051] bg-[#830051]" : "border-gray-300 bg-white"
          }`}
        >
          {selected && <i className="ri-check-line text-white text-[10px]"></i>}
        </span>
        <span className="flex-1">{opt}</span>
        {selected && !hideClearInDropdown && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              removeValue(opt);
            }}
            className="w-5 h-5 flex items-center justify-center rounded hover:bg-pink-100 cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-[12px] text-gray-500"></i>
          </span>
        )}
        {onOptionDelete && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              setDeleteConfirm(opt);
            }}
            className="w-5 h-5 flex items-center justify-center rounded hover:bg-red-50 cursor-pointer flex-shrink-0"
            title={isZh ? "删除此选项" : "Delete this option"}
          >
            <i className="ri-delete-bin-line text-[12px] text-red-500"></i>
          </span>
        )}
      </button>
    );
  };

  return (
    <div ref={ref} className="relative">
      {!hideLabel && label && (
        <label className="text-xs font-medium text-gray-600 flex items-center gap-1 mb-1.5">
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}
      {/* Trigger */}
      <div
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`w-full px-3.5 py-1.5 border rounded-lg flex items-center min-h-[34px] transition-colors ${
          disabled
            ? "bg-gray-50 border-gray-100 cursor-not-allowed"
            : open
              ? "border-[#830051] bg-white cursor-pointer"
              : "border-gray-200 bg-white hover:border-gray-300 cursor-pointer"
        }`}
      >
        <div className="flex-1 flex flex-wrap items-center gap-1.5 min-w-0">
          {values.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 text-xs bg-pink-50 rounded-md px-2 py-0.5 text-gray-700 border border-pink-100"
            >
              {v}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeValue(v);
                }}
                disabled={disabled}
                className="w-3.5 h-3.5 flex items-center justify-center rounded hover:bg-pink-100 cursor-pointer flex-shrink-0"
              >
                <i className="ri-close-line text-[10px] text-gray-500"></i>
              </button>
            </span>
          ))}
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={values.length === 0 ? placeholder : ""}
            className="flex-1 min-w-0 w-0 text-sm outline-none bg-transparent text-gray-700 placeholder-gray-400"
            disabled={disabled}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canCreate) {
                e.preventDefault();
                toggleValue(search.trim());
                setSearch("");
              } else if (e.key === "Escape") {
                setOpen(false);
                setSearch("");
              }
            }}
          />
        </div>
        <i
          className={`ri-arrow-down-s-line text-gray-400 flex-shrink-0 ml-2 transition-transform ${open ? "rotate-180" : ""}`}
        ></i>
      </div>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg z-20 overflow-hidden"
          style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}
        >
          <div className="max-h-56 overflow-y-auto">
            {groups && !search.trim() ? (
              /* Grouped view when no search */
              groups.map((group) => (
                <div key={group.label}>
                  <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide bg-gray-50/70 border-b border-gray-100">
                    {group.label}
                  </div>
                  {group.options.map((opt) => renderOptionRow(opt))}
                </div>
              ))
            ) : (
              /* Flat list when searching or no groups */
              <>
                {filtered.length === 0 && !canCreate && (
                  <div className="px-3 py-2.5 text-xs text-gray-400">
                    {isZh ? "暂无选项" : "No options available"}
                  </div>
                )}
                {filtered.map((opt) => renderOptionRow(opt))}
              </>
            )}

            {/* Create new */}
            {canCreate && (
              <button
                type="button"
                onClick={() => {
                  toggleValue(search.trim());
                  setSearch("");
                }}
                className="w-full px-3 py-2.5 text-sm text-left cursor-pointer hover:bg-pink-50 transition-colors border-t border-gray-100 flex items-center gap-2"
              >
                <i className="ri-add-line text-sm" style={{ color: "#830051" }}></i>
                <span className="text-gray-600">{isZh ? "创建" : "Create"} &ldquo;</span>
                <span className="font-semibold" style={{ color: "#830051" }}>
                  {search.trim()}
                </span>
                <span className="text-gray-600">&rdquo;</span>
              </button>
            )}
          </div>
          {values.length > 0 && (
            <div className="px-3 py-1.5 border-t border-gray-100 text-[11px] text-gray-400">
              {values.length} {isZh ? "个已选中" : "selected"}
            </div>
          )}
        </div>
      )}

      {/* Delete confirm dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm mx-4 p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-pink-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-alert-line text-[#830051] text-sm"></i>
              </div>
              <h3 className="text-sm font-semibold text-gray-800">
                {isZh ? "确认删除" : "Confirm Delete"}
              </h3>
            </div>
            <p className="text-xs text-gray-600 mb-4 leading-relaxed">
              {isZh
                ? `确定要删除选项 "${deleteConfirm}" 吗？此操作不可撤销。`
                : `Are you sure you want to delete "${deleteConfirm}"? This action cannot be undone.`}
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors cursor-pointer"
              >
                {isZh ? "取消" : "Cancel"}
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-3 py-1.5 text-xs text-white bg-[#830051] rounded-md hover:bg-[#6a0042] transition-colors cursor-pointer"
              >
                {isZh ? "删除" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
